/**
 * ChatTabContent
 *
 * The chat UI rendered inside the Chat tab of GlobalChatSidebar.
 * Extracted from ChatPanel — uses useChatSession() for all state.
 *
 * Wired to ElevenLabs components: MarthaOrb (animated orb in header),
 * BarVisualizer (frequency bars during voice), ShimmeringText (thinking state),
 * and SessionSummaryCard (post-call intelligence).
 */

import { Sparkles, History, Plus, X, Clock, FileText, LayoutGrid, Zap as SprintIcon, BarChart3, Cpu, ArrowUpRight, PanelRightClose, Settings, Palette, FileEdit } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { useChatSession } from '../../contexts/ChatSessionContext';
import { useChatSelection } from '../../contexts/ChatSelectionContext';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import { SessionList } from './SessionList';
import { MarthaOrb, type OrbState } from './MarthaOrb';
import { BarVisualizer } from './BarVisualizer';
import { ShimmeringText } from './ShimmeringText';
import { SessionSummaryCard } from './SessionSummaryCard';
import { useOpenAsTopic } from '../../hooks/useOpenAsTopic';
import { ChatPromoteToWorkStream } from './ChatPromoteToWorkStream';
import type { TabDefinition } from '../shared/ReaderDetailPanelShell';

/** Controls passed from GlobalChatSidebar so the coral bar can render page tabs */
export interface ChatTabHeaderControls {
  tabs: TabDefinition[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  onCollapse: () => void;
}

function voiceStatusToOrbState(voiceStatus: string | undefined, isStreaming: boolean): OrbState {
  if (voiceStatus === 'speaking') return 'speaking';
  if (voiceStatus === 'listening') return 'listening';
  if (isStreaming) return 'thinking';
  if (voiceStatus === 'connected' || voiceStatus === 'connecting') return 'listening';
  return 'idle';
}


/** Derives a human-readable page label + icon from the current route */
function getContextLabel(pathname: string, projectName: string | undefined): { icon: React.ReactNode; label: string } | null {
  const pathAfterWs = pathname.replace(/^\/w\/[^/]+/, '');

  // Archie pane
  if (pathAfterWs.startsWith('/ar/pi')) return { icon: <Cpu size={11} />, label: 'Pipeline Runs' };
  if (pathAfterWs.startsWith('/ar/mr')) return { icon: <Cpu size={11} />, label: 'Model Registry' };
  if (pathAfterWs.startsWith('/ar/ds')) return { icon: <Cpu size={11} />, label: 'Datasets' };
  if (pathAfterWs.startsWith('/ar/se')) return { icon: <Cpu size={11} />, label: 'Sessions' };
  if (pathAfterWs.startsWith('/ar/af')) return { icon: <Cpu size={11} />, label: 'Agent Fleet' };
  if (pathAfterWs.startsWith('/ar/ac')) return { icon: <Cpu size={11} />, label: 'Agent Catalog' };
  if (pathAfterWs.startsWith('/ar/dg')) return { icon: <Palette size={11} />, label: 'Design Studio' };
  if (pathAfterWs.startsWith('/ar/cf')) return { icon: <Settings size={11} />, label: 'Settings' };
  if (pathAfterWs.startsWith('/ar')) return { icon: <Cpu size={11} />, label: 'Archie' };

  // Success & Support
  if (pathAfterWs.startsWith('/su')) return { icon: <BarChart3 size={11} />, label: 'Success & Support' };

  // Sales & Marketing
  if (pathAfterWs.startsWith('/sm')) return { icon: <BarChart3 size={11} />, label: 'Sales & Marketing' };

  // Project-scoped routes
  const prefix = projectName ? `${projectName}: ` : '';
  if (pathAfterWs.includes('/documents/') || pathAfterWs.includes('/do/')) return { icon: <FileText size={11} />, label: `${prefix}Document` };
  if (pathAfterWs.includes('/documents') || pathAfterWs.includes('/do')) return { icon: <FileText size={11} />, label: `${prefix}Documentation` };
  if (pathAfterWs.includes('/bo/')) return { icon: <LayoutGrid size={11} />, label: `${prefix}Board` };
  if (pathAfterWs.includes('/bo')) return { icon: <LayoutGrid size={11} />, label: `${prefix}Boards` };
  if (pathAfterWs.includes('/ep')) return { icon: <LayoutGrid size={11} />, label: `${prefix}Epics` };
  if (pathAfterWs.includes('/sp')) return { icon: <SprintIcon size={11} />, label: `${prefix}Sprint Prep` };
  if (pathAfterWs.includes('/tl')) return { icon: <BarChart3 size={11} />, label: `${prefix}Telemetry` };
  if (pathAfterWs.includes('/me')) return { icon: <FileText size={11} />, label: `${prefix}Magic Editor` };
  if (pathAfterWs.includes('/bf')) return { icon: <Cpu size={11} />, label: `${prefix}Build Config` };
  if (pathAfterWs.includes('/cr')) return { icon: <Cpu size={11} />, label: `${prefix}Sprites` };
  if (pathAfterWs.includes('/qu')) return { icon: <LayoutGrid size={11} />, label: `${prefix}Queue` };

  if (pathAfterWs.startsWith('/pr/')) return { icon: <FileText size={11} />, label: projectName || 'Project' };

  return null;
}

/** Renders a hint with the agent name bolded */
function HintText({ text }: { text: string }) {
  // Bold the agent name (e.g. "Research Agent", "Martha", "Archie Platform")
  const match = text.match(/^(Use |Ask |Have )([\w\s]+ Agent|Martha|Archie Platform)( to | about | for | to | how | if | the | walk )/);
  if (!match) return <>{text}</>;
  const [, prefix, agentName, rest] = match;
  const remainder = text.slice(prefix.length + agentName.length + rest.length);
  return <>{prefix}<span className="font-semibold">{agentName}</span>{rest}{remainder}</>;
}

function SuggestedPrompts({ prompts, onSelect }: { prompts: string[]; onSelect: (p: string) => void }) {
  return (
    <div className="px-4 pb-3 pt-2 flex flex-col gap-1.5">
      {prompts.map((prompt, i) => (
        <button
          key={prompt}
          onClick={() => onSelect(prompt)}
          className="flex items-start gap-2.5 px-3.5 py-2.5 text-[11.5px] leading-relaxed text-left
                     text-stone-600 bg-white hover:bg-coral-50/50 rounded-xl
                     border border-stone-200/80 hover:border-coral-300/70
                     shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_rgb(var(--c-coral-500,_217_127_111)_/_0.12)]
                     transition-all duration-200 hover:-translate-y-[1px]
                     animate-fade-in"
          style={{ animationDelay: `${i * 100}ms`, animationFillMode: 'both' }}
        >
          <Sparkles size={11} className="text-coral-400 mt-[3px] flex-shrink-0" />
          <span><HintText text={prompt} /></span>
        </button>
      ))}
    </div>
  );
}

export function ChatTabContent({ headerControls }: { headerControls?: ChatTabHeaderControls } = {}) {
  const {
    messages,
    isStreaming,
    isVoiceActive,
    voiceStatus,
    audioLevel,
    inputFrequencyData,
    lastConversationId,
    dismissSummary,
    startVoice,
    stopVoice,
    sessionId,
    sessions,
    loadSession,
    startNewSession,
    archiveSession,
    isLoadingSessions,
    showSessionList,
    setShowSessionList,
    agentMode,
    setAgentMode,
    suggestedPrompts,
    showSuggestions,
    handleSend,
    cancelStream,
    queuedMessage,
    clearQueue,
    docContext,
    clearDocContext,
    toggle,
  } = useChatSession();

  const { selectedText, clearSelection } = useChatSelection();
  const { project } = useProject();
  const location = useLocation();

  const orbState = voiceStatusToOrbState(voiceStatus, isStreaming);
  const contextLabel = getContextLabel(location.pathname, project?.name);

  // Retry handler: re-send the last user message
  const handleRetry = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        handleSend(messages[i].content);
        break;
      }
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Mini header — coral Martha bar */}
      <div
        className="flex items-center justify-between px-3 py-2.5 flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111)) 0%, rgb(var(--c-coral-600, 198 102 86)) 50%, rgb(var(--c-coral-700, 165 78 65)) 100%)' }}
      >
        {/* Left: page tab icons (when other tabs exist) + orb + Martha label */}
        <div className="flex items-center gap-2">
          {headerControls && headerControls.tabs.length > 1 && (
            <div className="flex items-center gap-0.5 mr-1">
              {headerControls.tabs.map(tab => (
                <button
                  key={tab.id}
                  title={tab.title}
                  onClick={() => headerControls.onTabChange(tab.id)}
                  className={`relative p-1.5 rounded-md transition-colors ${
                    headerControls.activeTab === tab.id
                      ? 'bg-white/20 text-white'
                      : 'text-white/40 hover:text-white/70 hover:bg-white/10'
                  }`}
                >
                  <span className="[&>svg]:w-[14px] [&>svg]:h-[14px]">{tab.icon}</span>
                  {tab.badge && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-white/80 ring-1 ring-white/40" />
                  )}
                </button>
              ))}
              <div className="w-px h-4 bg-white/20 mx-0.5" />
            </div>
          )}
          <MarthaOrb state={orbState} audioLevel={audioLevel} size={24} />
          <div className="flex flex-col">
            <span className="font-semibold text-white text-xs leading-tight tracking-tight">Martha</span>
            {isStreaming && !isVoiceActive && (
              <span className="flex items-center gap-[3px] mt-0.5">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-1 h-1 rounded-full bg-white/70 animate-pulse"
                    style={{ animationDelay: `${i * 200}ms`, animationDuration: '1s' }}
                  />
                ))}
              </span>
            )}
            {isVoiceActive && voiceStatus && (
              <span className="text-[10px] text-white/60 leading-tight">
                {voiceStatus === 'speaking' ? 'speaking' : voiceStatus === 'listening' ? 'listening' : 'voice'}...
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <PromoteToTopicBtn />
          <button
            onClick={() => { startNewSession(); setShowSessionList(false); }}
            className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200"
            title="New conversation"
          >
            <Plus size={14} />
          </button>
          <button
            onClick={() => setShowSessionList(!showSessionList)}
            className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200"
            title="Conversation history"
          >
            <History size={14} />
          </button>
          <button
            onClick={headerControls ? headerControls.onCollapse : toggle}
            className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200"
            title="Collapse sidebar"
          >
            <PanelRightClose size={14} />
          </button>
        </div>
      </div>

      {/* Context bar — shows document context (priority) or current page */}
      {!showSessionList && (docContext || contextLabel) && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-100/80 flex-shrink-0"
          style={{ background: docContext ? 'rgb(var(--c-coral-500, 217 127 111) / 0.05)' : 'rgb(var(--c-surface-muted, 250 249 248) / 0.9)' }}
        >
          <span className={docContext ? 'text-coral-400' : 'text-neutral-400'}>
            {docContext ? <FileEdit size={11} /> : contextLabel?.icon}
          </span>
          <span className="text-[11.5px] truncate flex-1 font-medium" style={{ color: docContext ? 'rgb(var(--c-coral-600, 198 102 86))' : 'rgb(var(--c-text-subtle, 120 113 108))' }}>
            {docContext
              ? (docContext.document_title || 'Document loaded')
              : contextLabel?.label}
          </span>
          {docContext && (
            <button
              onClick={clearDocContext}
              className="text-coral-300 hover:text-coral-500 transition-colors flex-shrink-0"
              title="Clear document context"
            >
              <X size={11} />
            </button>
          )}
        </div>
      )}

      {/* Body */}
      {showSessionList ? (
        <SessionList
          sessions={sessions}
          activeSessionId={sessionId}
          isLoading={isLoadingSessions}
          onSelect={loadSession}
          onNewSession={startNewSession}
          onArchive={archiveSession}
          onClose={() => setShowSessionList(false)}
        />
      ) : (
        <ChatMessageList messages={messages} isStreaming={isStreaming} onRetry={handleRetry} />
      )}

      {/* Chat→WorkStream promotion affordance (WORKSTREAMS_FROM_CHAT). Renders
          only when the thread has a promotable shape (≥2 non-system messages).
          martha-v5 has no topic-suggestions endpoint yet, so the affordance
          falls back to the chat thread title as suggested_name (Story 3 AC-5). */}
      {sessionId && messages.filter(m => m.role !== 'system').length >= 2 && !showSessionList && (
        <ChatPromoteToWorkStream
          fallbackSuggestedName={
            messages.find(m => m.role === 'user')?.content?.slice(0, 60).trim() || null
          }
          className="flex-shrink-0 px-4 pt-2"
        />
      )}

      {/* Auxiliary sections — capped height so they don't starve the message list */}
      <div className="flex-shrink-0 overflow-y-auto" style={{ maxHeight: '30vh' }}>
        {/* Document context welcome card — shown when doc is loaded but no messages yet */}
        {docContext && messages.filter(m => m.role !== 'system').length === 0 && !showSessionList && (
          <div className="px-4 pt-4 pb-1.5">
            <div
              className="rounded-xl px-3.5 py-3 border"
              style={{
                background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111) / 0.06) 0%, rgb(var(--c-coral-600, 198 102 86) / 0.04) 100%)',
                borderColor: 'rgb(var(--c-coral-500, 217 127 111) / 0.2)',
              }}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111)) 0%, rgb(var(--c-coral-600, 198 102 86)) 100%)' }}
                >
                  <FileEdit size={13} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-stone-700 mb-0.5 truncate">
                    {docContext.document_title || 'Document ready'}
                  </p>
                  <p className="text-[11px] text-stone-500 leading-relaxed">
                    Martha has read this document and is ready to help you write, edit, or improve it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSuggestions && !showSessionList && !docContext && (
          <SuggestedPrompts prompts={suggestedPrompts} onSelect={handleSend} />
        )}

        {/* Selection chip */}
        {selectedText && (
          <div className="px-4 pb-1.5 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs
                             bg-blue-50 text-blue-700 border border-blue-200/60 rounded-lg
                             max-w-full truncate shadow-sm">
              Selected: {selectedText.length > 60 ? selectedText.slice(0, 60) + '...' : selectedText}
              <button
                onClick={clearSelection}
                className="ml-1 text-blue-400 hover:text-blue-600 transition-colors"
                title="Clear selection"
              >
                <X size={12} />
              </button>
            </span>
          </div>
        )}

        {/* Queued message chip */}
        {queuedMessage && (
          <div className="px-4 pb-1.5 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs
                             bg-violet-50 text-violet-700 border border-violet-200/60 rounded-lg
                             max-w-full truncate shadow-sm">
              <Clock size={11} className="text-violet-400 flex-shrink-0" />
              Queued: {queuedMessage.length > 50 ? queuedMessage.slice(0, 50) + '...' : queuedMessage}
              <button
                onClick={clearQueue}
                className="ml-1 text-violet-400 hover:text-violet-600 transition-colors"
                title="Clear queued message"
              >
                <X size={12} />
              </button>
            </span>
          </div>
        )}

        {/* Post-call session summary (after voice session ends) */}
        {lastConversationId && !isVoiceActive && (
          <SessionSummaryCard
            conversationId={lastConversationId}
            onDismiss={dismissSummary}
          />
        )}

        {/* Bar visualizer when voice is active */}
        {isVoiceActive && (
          <div className="px-3 py-1.5 flex items-center justify-center">
            <BarVisualizer
              frequencyData={inputFrequencyData}
              audioLevel={audioLevel}
              barCount={7}
              height={20}
            />
          </div>
        )}
      </div>

      <ChatInput
        onSend={handleSend}
        disabled={false}
        messages={messages}
        agentMode={agentMode}
        onToggleAgentMode={() => setAgentMode(!agentMode)}
        isStreaming={isStreaming}
        onCancel={cancelStream}
        isVoiceActive={isVoiceActive}
        onStartVoice={startVoice}
        onStopVoice={stopVoice}
      />
    </div>
  );
}

// ── Promote to Topic ────────────────────────────────────────────────────────

function PromoteToTopicBtn() {
  const { sessionId, messages } = useChatSession();
  const { openAsTopic, isCreating } = useOpenAsTopic();

  if (!sessionId || messages.length < 2) return null;

  // Derive title from first user message
  const firstUserMsg = messages.find(m => m.role === 'user');
  const title = firstUserMsg
    ? (firstUserMsg.content ?? '').slice(0, 60).trim() || 'Chat Session'
    : 'Chat Session';

  return (
    <button
      onClick={() =>
        openAsTopic({
          type: 'general',
          title,
          object_type: 'talk_session',
          object_id: sessionId,
          item: {
            item_type: 'conversation',
            item_id: sessionId,
            label: title,
          },
        })
      }
      disabled={isCreating}
      className="p-1 text-white/50 hover:text-white hover:bg-white/10 rounded-md transition-all duration-200"
      title="Promote to Topic"
    >
      <ArrowUpRight size={14} />
    </button>
  );
}
